import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import PauseCircleRoundedIcon from '@mui/icons-material/PauseCircleRounded';
import {
  Alert,
  Box, Button,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogContent,
  Divider,
  FormHelperText,
  IconButton,
  InputBase,
  Paper,
  Snackbar,
  ToggleButton, ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useStripe } from "@stripe/react-stripe-js";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useEffect, useState } from 'react';
import * as send from '../../../../lib/analytics/send';
import { dataConstants } from "../../../constants";
import useAuth from "../../../hook/auth";
import { checkSubscription, createCheckout } from "../../../utils/helpers";
import { colors, shadows, radii, pricing as pricingTokens } from "../../../utils/designTokens";
import Coupon from "../CouponPop";

// -----------------------------------------------------------------------------
// While this flag is `true`, clicking "Start Premium" shows a dialog
// instead of starting checkout. The Stripe checkout flow below is left fully
// untouched — change this back to `false` to resume sign-ups.
// -----------------------------------------------------------------------------
const SUBSCRIPTIONS_PAUSED = true;

const BillingToggle = styled(ToggleButtonGroup)(() => ({
  background: colors.neutral[100],
  borderRadius: `${radii.pill}px`,
  padding: 3,
  '.MuiToggleButton-root.Mui-selected': {
    background: '#ffffff',
    boxShadow: pricingTokens.toggle.selectedShadow,
    color: colors.neutral[900],
    borderRadius: `${radii.pill}px !important`,
    '&:hover': { background: '#ffffff' },
  },
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    lineHeight: 1,
    padding: '10px 22px',
    color: colors.neutral[500],
    fontWeight: 600,
    fontSize: '0.875rem',
    border: 0,
    borderRadius: `${radii.pill}px !important`,
  },
  '& .MuiToggleButtonGroup-grouped': {
    border: 0,
    '&.Mui-disabled': { border: 0 },
  },
}));

function splitPrice(value) {
  if (value === 0) return ['0', '00'];
  const str = Number(value).toFixed(2);
  return str.split('.');
}

function savingsPercent(monthly, yearly) {
  if (!monthly || !yearly) return 0;
  return Math.round((1 - yearly / (monthly * 12)) * 100);
}

const PremiumPlan = ({ pricePlans }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth() || {};
  const router = useRouter();
  const stripe = useStripe();

  const { reloadUserInfo } = user || {};
  const { customAttributes, email } = reloadUserInfo || {};
  const { userType, stripe_customer_id } = customAttributes ? JSON.parse(customAttributes) : {};
  const isPremium = userType === "PREMIUM" || userType === "PROFESSIONAL";

  const [plans, setPlans] = useState(
    pricePlans || (process.env.NODE_ENV !== 'production' ? dataConstants.DEVPLANS : dataConstants.PRODPLANS)
  );
  const premiumPlan = plans?.length > 0 ? plans.find(d => d.plan.toLowerCase() === 'premium') : null;
  const premiumFeatures = premiumPlan?.features ? JSON.parse(premiumPlan.features) : {};

  const [planCycle, setPlanCycle] = useState('Annually');
  const [priceID, setPriceID] = useState(premiumPlan?.ypid);
  const [priceIdLoading, setPriceIdLoading] = useState();
  const [hasPastSubscriptions, setPastSubscriptions] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [pausedDialogOpen, setPausedDialogOpen] = useState(false);

  const [showCoupon, setShowCoupon] = useState(false);
  const [coupon, setCoupon] = useState({
    hasCode: false, hasCoupon: false,
    code: '', loading: false, isValid: false,
  });

  const monthlyPrice = premiumPlan?.m || 0;
  const yearlyTotal = coupon?.y_final_amount ?? premiumPlan?.y ?? 0;
  const yearlyPerMonth = yearlyTotal === 0 ? [0, '00'] : splitPrice(yearlyTotal / 12);
  const savings = savingsPercent(monthlyPrice, premiumPlan?.y);

  const handleCycle = (_event, newCycle) => {
    if (newCycle !== null) setPlanCycle(newCycle);
  };

  const handleBillingChange = (newCycle) => {
    setPriceID(newCycle === 'Annually' ? premiumPlan?.ypid : premiumPlan?.mpid);
  };

  const handleCoupon = (prop) => (event) => {
    if (coupon) {
      delete coupon.m_final_amount;
      delete coupon.y_final_amount;
      setCoupon({ ...coupon, [prop]: event.target.value, loading: false, isValid: false });
    }
  };

  const handleCouponCode = async () => {
    setShowCoupon(!coupon?.errText);
  };

  const enterCoupon = (e) => {
    setCoupon({ ...coupon, hasCoupon: !coupon.hasCoupon });
    if (e) send.uevent(e);
  };

  const getCoupon = async (event) => {
    event.preventDefault();
    setCoupon({ ...coupon, loading: true });
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_PAY_URL}/promotion?code=${coupon?.code}`);
      if (!res.ok) {
        const text = await res.text();
        setCoupon({ ...coupon, errText: text });
        throw new Error(text);
      }
      const d = await res.json();
      const data = d?.data?.[0];
      if (!data || d?.data?.length <= 0) {
        setCoupon({ ...coupon, errText: 'Not Found' });
      } else {
        setCoupon({
          ...coupon,
          ...data?.coupon,
          isValid: data?.coupon?.id === coupon?.code ? data?.coupon?.valid ?? false : false,
          loading: false,
          m_final_amount: Math.round(((premiumPlan?.y / 12) * data?.coupon?.percent_off) / 100) ?? null,
          y_final_amount: Math.round(premiumPlan?.y * (1 - data?.coupon?.percent_off / 100)),
        });
      }
      handleCouponCode();
    } catch (err) {
      console.log('coupon error:', err);
    }
  };

  const redeemCoupon = async (event, _cou) => {
    event.preventDefault();
    setCoupon({ ...coupon, loading: true });
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_PAY_URL}/promotion?code=${_cou}`);
      if (!res.ok) {
        const text = await res.text();
        setCoupon({ ...coupon, errText: text });
        throw new Error(text);
      }
      const d = await res.json();
      const data = d?.data?.[0];
      if (!data || d?.data?.length <= 0) {
        setCoupon({ ...coupon, errText: 'Not Found' });
      } else {
        setCoupon({
          ...coupon,
          ...data?.coupon,
          isValid: data?.coupon?.id === _cou ? data?.coupon?.valid ?? false : false,
          loading: false,
          m_final_amount: Math.round(((premiumPlan?.y / 12) * data?.coupon?.percent_off) / 100) ?? null,
          y_final_amount: Math.round(premiumPlan?.y * (1 - data?.coupon?.percent_off / 100)),
        });
      }
      handleCouponCode();
    } catch (err) {
      console.log('redeem error:', err);
    }
  };

  const handleCheckout = async (checkoutPriceID) => {
    if (!user) {
      return router.push({ pathname: '/auth', query: { from: router.asPath } });
    }

    if (isPremium) {
      setPriceIdLoading(checkoutPriceID);
      try {
        const res = await fetch('/api/create-portal-link', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stripe_customer_id }),
        });
        const data = await res.json();
        if (data?.url) { window.location.href = data.url; return; }
        throw new Error(data?.error?.message || 'Could not open billing portal');
      } catch (error) {
        setCheckoutError(error.message);
        setPriceIdLoading(undefined);
        return;
      }
    }

    if (SUBSCRIPTIONS_PAUSED) {
      setPausedDialogOpen(true);
      return;
    }

    setPriceIdLoading(checkoutPriceID);

    // Safely resolve the email. Check reloadUserInfo first, fallback to user object.
    const userEmail = email || user?.email;

    // Prevent silent failure. Alert the user if the email is missing.
    if (!userEmail) {
      setCheckoutError("We couldn't find an email associated with your account. Please sign out and sign back in.");
      setPriceIdLoading(undefined);
      return;
    }

    try {
      const { url, error } = await createCheckout({
        url: '/api/create-checkout-session',
        priceID: checkoutPriceID,
        email: userEmail,
        coupon: planCycle === 'Annually' ? (coupon?.id || '') : '',
        oldUser: (stripe_customer_id && userType === "FREE") || hasPastSubscriptions,
      });

      // Check if the helper function returned a backend error
      if (error) throw new Error(error.message);

      // Ensure URL exists before redirecting, otherwise throw an error
      if (url) {
        window.location.href = url;
      } else {
        throw new Error("Failed to generate the checkout link. Please try again.");
      }
    } catch (error) {
      setCheckoutError(error?.message || 'Something went wrong. Please try again.');
    } finally {
      setPriceIdLoading(undefined);
    }
  };

  useEffect(() => {
    if (!stripe_customer_id && email) {
      checkSubscription(email)
        .then(d => setPastSubscriptions(d?.hasPastSubscriptions))
        .catch(err => console.error('subscription err', err.message));
    }
  }, [stripe_customer_id, email]);

  const isYearly = planCycle === 'Annually';
  const activePriceID = isYearly ? premiumPlan?.ypid : premiumPlan?.mpid;
  const featureHighlights = premiumFeatures?.features?.slice(0, 5) || [
    'Stock screener with saved screens',
    'Five-minute due diligence',
    'Fund holdings & activity',
    'US economy dashboard',
    'Daily stock discovery',
  ];

  return (
    <>
      <Box
        id="plans"
        sx={{
          scrollMarginTop: '80px',
          pb: { xs: 6, md: 6 },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5 }}>
          <BillingToggle
            size="small"
            value={planCycle}
            exclusive
            onChange={(e, newCycle) => {
              handleCycle(e, newCycle);
              if (newCycle) handleBillingChange(newCycle);
            }}
            aria-label="billing-cycle"
          >
            <ToggleButton value="Annually" aria-label="yearly">
              Yearly
              {savings > 0 && (
                <Chip
                  label={`Save ${savings}%`}
                  size="small"
                  sx={{
                    ml: 1,
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    bgcolor: colors.success.bg,
                    color: colors.success.dark,
                    border: `1px solid ${colors.success.main}20`,
                  }}
                />
              )}
            </ToggleButton>
            <ToggleButton value="Monthly" aria-label="monthly">
              Monthly
            </ToggleButton>
          </BillingToggle>
        </Box>

        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={(e) => { e.preventDefault(); handleCheckout(activePriceID); send.uevent(e); }}
          data-event-name={isPremium ? 'manage-sub' : 'buy-subscription'}
          data-event-category="pricing"
          data-event-label={`${planCycle}-plan`}
          sx={{
            maxWidth: 480,
            mx: 'auto',
            p: { xs: 3, md: 4 },
            borderRadius: `${radii.lg}px`,
            border: isYearly ? `1px solid ${colors.accent.main}` : `1px solid ${colors.neutral[200]}`,
            boxShadow: shadows.md,
            bgcolor: '#ffffff',
            transition: 'box-shadow 200ms ease',
            '&:hover': { boxShadow: shadows.lg },
            position: 'relative'
          }}
        >
          {isYearly && (
            <Chip
              label="RECOMMENDED"
              size="small"
              sx={{
                mb: 2.5,
                fontWeight: 700,
                fontSize: '0.7rem',
                letterSpacing: '0.05em',
                bgcolor: pricingTokens.badge.bg,
                color: pricingTokens.badge.color,
                position: 'absolute',
                top: '0%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            />
          )}

          <Typography variant="h5" sx={{ fontWeight: 700, color: colors.neutral[900], mb: 0.5 }}>
            Premium
          </Typography>
          <Typography variant="body2" sx={{ color: colors.neutral[500], mb: 3 }}>
            Full access to all Grufity tools and data.
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '2.75rem', lineHeight: 1, color: colors.neutral[900] }}>
              ${isYearly ? yearlyPerMonth[0] : splitPrice(monthlyPrice)[0]}
            </Typography>
            <Typography sx={{ fontWeight: 600, fontSize: '1.25rem', color: colors.neutral[500], ml: 0.3 }}>
              .{isYearly ? yearlyPerMonth[1] : splitPrice(monthlyPrice)[1]}
            </Typography>
            <Typography sx={{ fontWeight: 500, fontSize: '0.95rem', color: colors.neutral[400], ml: 0.75 }}>
              /month
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            {isYearly ? (
              <Typography variant="body2" sx={{ color: colors.neutral[500] }}>
                {coupon?.y_final_amount >= 0 && coupon?.y_final_amount !== premiumPlan?.y ? (
                  <>
                    <Typography component="span" sx={{ textDecoration: 'line-through', color: colors.neutral[400], fontSize: 'inherit' }}>
                      ${premiumPlan?.y}
                    </Typography>
                    {' '}
                    <Typography component="span" sx={{ color: colors.success.main, fontWeight: 600, fontSize: 'inherit' }}>
                      ${coupon.y_final_amount} billed yearly
                    </Typography>
                  </>
                ) : (
                  `$${premiumPlan?.y} billed yearly`
                )}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: colors.neutral[500] }}>
                ${monthlyPrice} billed monthly
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            {featureHighlights.map((f, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', py: 0.75 }}>
                <CheckRoundedIcon sx={{ fontSize: '1.1rem', color: colors.success.main, mr: 1.5, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: colors.neutral[700] }}>{f}</Typography>
              </Box>
            ))}
          </Box>

          <Button
            variant="contained"
            type="submit"
            fullWidth
            disabled={!!priceIdLoading}
            disableElevation
            sx={{
              py: 1.5,
              fontWeight: 700,
              fontSize: '0.95rem',
              textTransform: 'none',
              borderRadius: `${radii.sm}px`,
              bgcolor: pricingTokens.cta.bg,
              '&:hover': { bgcolor: pricingTokens.cta.hoverBg },
            }}
          >
            {priceIdLoading
              ? <CircularProgress size={22} sx={{ color: 'inherit' }} />
              : isPremium ? 'Manage Subscription' : 'Start Premium'}
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2, gap: 0.5 }}>
            <LockRoundedIcon sx={{ fontSize: '0.85rem', color: colors.neutral[400] }} />
            <Typography variant="caption" sx={{ color: colors.neutral[400] }}>
              Cancel anytime&ensp;·&ensp;Secure checkout via Stripe
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            {showCoupon && coupon?.code ? (
              <Box sx={{ textAlign: 'center' }}>
                <Chip
                  sx={{
                    borderRadius: `${radii.sm}px`,
                    '& .MuiChip-label': { fontWeight: 600, mx: 1 },
                    bgcolor: !isYearly ? colors.error.bg : colors.success.bg,
                    border: `1px solid ${!isYearly ? colors.error.main : colors.success.main}30`,
                  }}
                  icon={<SellRoundedIcon fontSize="small" sx={{ fontSize: '1rem !important' }} />}
                  label={coupon?.code ?? ''}
                  onDelete={() => {
                    setCoupon({ hasCode: false, code: '', loading: false, isValid: false });
                    setShowCoupon(false);
                  }}
                  deleteIcon={<ClearRoundedIcon fontSize="small" sx={{ fontSize: '1.1rem !important' }} />}
                />
                <FormHelperText sx={{ color: !isYearly || coupon?.errText ? colors.error.main : colors.success.main, textAlign: 'center' }}>
                  {!isYearly ? 'Valid only on yearly plan' : (coupon?.name ?? coupon?.errText)}
                </FormHelperText>
              </Box>
            ) : !coupon.hasCoupon ? (
              <Button
                onClick={enterCoupon}
                disabled={!isYearly}
                sx={{
                  my: 0.5,
                  textTransform: 'none',
                  color: colors.accent.main,
                  fontSize: '0.8rem',
                  fontWeight: 500,
                }}
                data-event-name="add-coupon"
                data-event-category="pricing"
              >
                Have a coupon code?
              </Button>
            ) : (
              <Box sx={{ maxWidth: 260, mx: 'auto' }}>
                <ClickAwayListener
                  mouseEvent="onMouseDown"
                  touchEvent="onTouchStart"
                  onClickAway={(e) => coupon?.errText ? e.preventDefault() : enterCoupon()}
                >
                  <Paper
                    elevation={0}
                    component="form"
                    sx={{
                      p: '2px 4px',
                      display: 'flex',
                      alignItems: 'center',
                      border: `1px solid ${colors.neutral[200]}`,
                      borderRadius: `${radii.sm}px`,
                    }}
                    onSubmit={getCoupon}
                  >
                    <InputBase
                      id="coupon-code"
                      sx={{ ml: 1, flex: 1, fontSize: '0.85rem' }}
                      placeholder="Enter coupon code"
                      value={coupon.code}
                      onChange={handleCoupon('code')}
                      inputProps={{ 'aria-label': 'coupon code' }}
                    />
                    <Divider sx={{ height: 28, m: 0.5, borderColor: colors.neutral[100] }} orientation="vertical" />
                    {coupon?.valid ? (
                      <IconButton color="success" sx={{ p: '5px', ml: 1 }} aria-label="verified">
                        <DoneRoundedIcon />
                      </IconButton>
                    ) : coupon?.loading ? (
                      <CircularProgress sx={{ p: '5px', width: '30px !important', height: '30px !important', ml: 1 }} />
                    ) : (
                      <Button
                        size="small"
                        sx={{ color: colors.accent.main, textTransform: 'none', fontWeight: 600 }}
                        aria-label="apply coupon"
                        onClick={getCoupon}
                        type="submit"
                      >
                        Apply
                      </Button>
                    )}
                  </Paper>
                </ClickAwayListener>
                <FormHelperText sx={{ color: coupon?.valid ? colors.success.main : colors.neutral[400], ml: 0.5, textAlign: 'center' }}>
                  {coupon?.valid ? 'Coupon applied successfully' : (coupon?.errText ?? 'Press Enter to apply')}
                </FormHelperText>
              </Box>
            )}
          </Box>

          <Typography variant="caption" component="div" sx={{ textAlign: 'center', mt: 2, color: colors.neutral[400], '& a': { color: colors.accent.main, textDecoration: 'none' } }}>
            By subscribing you agree to our <Link href="/terms">Terms</Link> and <Link href="/privacy">Privacy Policy</Link>.
          </Typography>
        </Box>
      </Box>

      <Coupon onClick={redeemCoupon} premiumFeatures={premiumFeatures} loading={coupon?.loading} />

      <Snackbar
        open={!!checkoutError}
        autoHideDuration={7000}
        onClose={() => setCheckoutError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setCheckoutError(null)} sx={{ width: '100%' }}>
          {checkoutError}
        </Alert>
      </Snackbar>

      <Dialog
        open={pausedDialogOpen}
        onClose={() => setPausedDialogOpen(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="xs"
        aria-labelledby="subscriptions-paused-title"
        aria-describedby="subscriptions-paused-description"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : `${radii.lg}px`,
            boxShadow: shadows.lg,
          },
        }}
      >
        <IconButton
          onClick={() => setPausedDialogOpen(false)}
          aria-label="Close"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: colors.neutral[400],
            '&:hover': { color: colors.neutral[700], bgcolor: colors.neutral[100] },
          }}
        >
          <ClearRoundedIcon fontSize="small" />
        </IconButton>

        <DialogContent
          sx={{
            textAlign: 'center',
            px: { xs: 3.5, sm: 5 },
            py: { xs: 6, sm: 6 },
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: `${colors.accent.main}14`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <PauseCircleRoundedIcon sx={{ fontSize: 32, color: colors.accent.main }} />
          </Box>

          <Typography
            id="subscriptions-paused-title"
            variant="h6"
            sx={{ fontWeight: 700, color: colors.neutral[900], mb: 1 }}
          >
            New subscriptions are paused
          </Typography>

          <Typography
            id="subscriptions-paused-description"
            variant="body2"
            sx={{ color: colors.neutral[500], lineHeight: 1.65, mb: 4, mt: 1 }}
          >
            We're temporarily pausing new sign-ups while we make things even better.
            We'd love to have you on board, so please check back in a little while.
          </Typography>

          <Button
            variant="contained"
            fullWidth
            disableElevation
            onClick={() => setPausedDialogOpen(false)}
            sx={{
              pt: 1.4,
              mt: 3,
              fontWeight: 700,
              fontSize: '0.95rem',
              textTransform: 'none',
              borderRadius: `${radii.sm}px`,
              bgcolor: pricingTokens.cta.bg,
              '&:hover': { bgcolor: pricingTokens.cta.hoverBg },
            }}
          >
            Got it
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PremiumPlan;
